import { useToast } from '@chakra-ui/react';
import { ApolloError } from '@apollo/client';
import { useCallback } from 'react';

export interface GraphQLErrorExtensions {
  code?: string;
  userMessage?: string;
  debugMessage?: string;
}

export const useErrorHandler = () => {
  const toast = useToast();

  const handleError = useCallback(
    (error: ApolloError | Error) => {
      // Check if it's an Apollo error with GraphQL errors
      if (error instanceof ApolloError && error.graphQLErrors?.length > 0) {
        error.graphQLErrors.forEach((graphQLError) => {
          const extensions = graphQLError.extensions as GraphQLErrorExtensions;

          // Use the userMessage if available, otherwise fall back to the error message
          const message = extensions?.userMessage || graphQLError.message;

          // Determine the toast status based on the error code
          let status: 'error' | 'warning' | 'info' = 'error';
          if (extensions?.code === 'FORBIDDEN') {
            status = 'warning';
          }

          toast({
            title: 'Error',
            description: message,
            status,
            duration: 10000,
            isClosable: true,
            position: 'top-right',
          });
        });
      } else if (error instanceof ApolloError && error.networkError) {
        // Handle network errors
        toast({
          title: 'Network Error',
          description:
            'Unable to connect to the server. Please check your connection.',
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'top-right',
        });
      } else {
        // Handle generic errors
        toast({
          title: 'Error',
          description: error.message || 'An unexpected error occurred',
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'top-right',
        });
      }
    },
    [toast]
  );

  return { handleError };
};
